import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import DescriptionEditor from '@wix/da-shared-react/pkg/DescriptionEditor';
import { EditorTiptapContentReadyHandler } from '@wix/da-shared-react/pkg/DescriptionEditor/DescriptionEditor';
import { EditorApi } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { UseEditorOptions } from '@wix/da-editor-tiptap/pkg/hooks/useEditor';
import { getContentFromStorage } from '@wix/da-editor-tiptap/pkg/components/utils/tiptap';
import { OriginalDeviationFieldName } from '../../types';

interface Props {}

const DescriptionField: React.FC<Props> = () => {
  const { t } = useTranslation();
  const id = useId();
  const isMobile = useContext(MobileContext);
  const { control, errors, setValue, getValues, clearErrors } =
    useFormContext();
  const {
    field: { value },
  } = useController({
    name: OriginalDeviationFieldName.DESCRIPTION,
    control,
  });
  const error = errors[OriginalDeviationFieldName.DESCRIPTION];

  // As generating JSON is expensive for every typing event, we lazily resolve
  // the editors save state on submit rather than on typing.
  const lazyEditorStateValueResolver = useRef<() => string>(undefined);
  const handleInitialContentReady: EditorTiptapContentReadyHandler =
    useCallback(
      (api: EditorApi) => {
        lazyEditorStateValueResolver.current = () => {
          return JSON.stringify(api.getStateForSave());
        };
        setValue(
          OriginalDeviationFieldName.DESCRIPTION,
          lazyEditorStateValueResolver.current,
          { shouldDirty: false }
        );
      },
      [setValue]
    );

  const handleChange: UseEditorOptions['onEditorChange'] = useCallback(
    editorState => {
      clearErrors(OriginalDeviationFieldName.DESCRIPTION);
      setValue(
        OriginalDeviationFieldName.DESCRIPTION,
        getValues(OriginalDeviationFieldName.DESCRIPTION),
        { shouldDirty: true }
      );
    },
    [clearErrors, getValues, setValue]
  );

  const initialContent = useMemo(() => {
    return typeof value === 'string' ? JSON.parse(value) : value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile description editor doesn't set initial content until
  // tapped into the first time, force the value beforehand
  useEffect(() => {
    if (isMobile) {
      setValue(
        OriginalDeviationFieldName.DESCRIPTION,
        () => JSON.stringify({
          version: '1',
          document: getContentFromStorage(initialContent),
        }),
        { shouldDirty: false }
      );
    }
  }, []);

  return (
    <FormElement
      inputId={id}
      label={
        <InputLabelText isRequired>
          {t('originalDeviationPopup.form.description.label')}
        </InputLabelText>
      }
      noBottomSpacing
    >
      <DescriptionEditor
        placeholder={t('originalDeviationPopup.form.description.placeholder')}
        initialContent={initialContent}
        onChange={handleChange}
        onInitialContentReady={handleInitialContentReady}
        hasError={!!error}
      />
      {error && <InputErrorMessage>{error.message}</InputErrorMessage>}
    </FormElement>
  );
};
DescriptionField.displayName = 'DescriptionField';

export default DescriptionField;
